import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, FileText, Mail, MapPin } from "lucide-react";

const EFFECTIVE_DATE = "February 1, 2026";
const COMPANY = "Kosansh Solutions Inc";
const DBA = "ActivitySports.com";
const EMAIL = "techieroshan@gmail.com";
const ADDRESS = "Fort Worth, TX, United States";
const GOVERNING_LAW = "State of Texas";

const sections = [
  { id: "acceptance", title: "1. Acceptance of Terms" },
  { id: "description", title: "2. Description of Service" },
  { id: "eligibility", title: "3. Eligibility & Accounts" },
  { id: "subscriptions", title: "4. Subscriptions & Payments" },
  { id: "conduct", title: "5. Acceptable Use Policy" },
  { id: "user-content", title: "6. User-Generated Content" },
  { id: "intellectual-property", title: "7. Intellectual Property" },
  { id: "third-parties", title: "8. Third-Party Services" },
  { id: "disclaimers", title: "9. Disclaimers" },
  { id: "liability", title: "10. Limitation of Liability" },
  { id: "indemnification", title: "11. Indemnification" },
  { id: "governing-law", title: "12. Governing Law & Disputes" },
  { id: "termination", title: "13. Termination" },
  { id: "general", title: "14. General Provisions" },
  { id: "contact", title: "15. Contact" },
];

export default function TermsOfServicePage() {
  const [active, setActive] = useState("acceptance");
  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Hero */}
      <div className="bg-brand-navy text-white py-14 px-4 dark-section">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 text-brand-green text-sm font-bold uppercase tracking-widest mb-3">
            <FileText size={14} /> Legal
          </div>
          <h1 className="text-4xl md:text-5xl font-hero font-black mb-3">Terms of Service</h1>
          <p className="text-white/70 max-w-2xl">
            These Terms govern your access to and use of {DBA}. Please read them carefully before
            creating an account or using any part of the Platform.
          </p>
          <p className="text-white/50 text-sm mt-4">Effective: {EFFECTIVE_DATE} &nbsp;·&nbsp; Last Updated: {EFFECTIVE_DATE}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12 flex flex-col lg:flex-row gap-10">
        {/* Sidebar TOC */}
        <aside className="lg:w-64 flex-shrink-0">
          <div className="bg-white border border-gray-200 rounded-2xl p-5 sticky top-24 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Contents</p>
            <nav className="space-y-1">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  onClick={() => setActive(s.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    active === s.id
                      ? "bg-brand-navy/5 text-brand-navy font-semibold"
                      : "text-gray-500 hover:text-brand-navy hover:bg-gray-50"
                  }`}
                >
                  <ChevronRight size={12} className="flex-shrink-0" />
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <article className="flex-1 max-w-3xl space-y-12">

          <Section id="acceptance" title="1. Acceptance of Terms">
            <p>
              By accessing or using any part of the {DBA} platform, website, or services (collectively,
              the "Platform"), you agree to be bound by these Terms of Service ("Terms"), our{" "}
              <Link to="/privacy" className="text-brand-green underline underline hover:underline">Privacy Policy</Link>,{" "}
              <Link to="/community" className="text-brand-green underline underline hover:underline">Community Guidelines</Link>, and{" "}
              <Link to="/safety" className="text-brand-green underline underline hover:underline">Safety Center</Link>. These Terms
              constitute a legally binding agreement between you ("User," "you," or "your") and{" "}
              <strong>{COMPANY}</strong>, a Texas corporation ("Company," "we," "us," or "our").
            </p>
            <p className="mt-3">
              If you do not agree to these Terms, you must not create an account, access, or use the Platform.
              By clicking "Create Free Account," "Sign In," or otherwise using the Platform, you confirm
              that you have read, understood, and agree to these Terms.
            </p>
            <p className="mt-3">
              We reserve the right to modify these Terms at any time. We will provide at least 30 days'
              advance notice of material changes via email or prominent in-app notification. Continued
              use after the effective date of changes constitutes acceptance.
            </p>
          </Section>

          <Section id="description" title="2. Description of Service">
            <p>
              {DBA} is a sports partner matching platform operated by {COMPANY} that enables users to:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm mt-3">
              <li>Create and maintain a sports profile with skill levels, sport preferences, and availability</li>
              <li>Discover and connect with compatible sports partners in their geographic area</li>
              <li>Browse and discover sports venues across 50+ US cities</li>
              <li>Send and receive play session invitations and confirmations</li>
              <li>Communicate via in-app messaging with matched players</li>
              <li>Participate in corporate wellness sports leagues</li>
              <li>Join or create sports clubs with community announcements</li>
              <li>Access AI-powered match explanations (Gemini AI)</li>
            </ul>
            <p className="mt-3">
              The Platform is provided "as a facilitator" — we connect users but we are <strong>not a party</strong> to
              any sports session, agreement, or arrangement between users. We are not responsible for the actions,
              conduct, quality, or safety of any user or venue.
            </p>
          </Section>

          <Section id="eligibility" title="3. Eligibility & Accounts">
            <SubHead>3.1 Eligibility</SubHead>
            <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm">
              <li>You must be at least 13 years of age. Users aged 13–17 require verifiable parental consent.</li>
              <li>You must be a human. Automated or bot accounts are prohibited.</li>
              <li>If using on behalf of a company (e.g., corporate wellness leagues), you confirm authorization to bind that company.</li>
            </ul>
            <SubHead>3.2 Account Registration</SubHead>
            <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm">
              <li>You must provide accurate, current, and complete registration information.</li>
              <li>You are responsible for maintaining the confidentiality of your credentials.</li>
              <li>You must notify us immediately of any unauthorized access to your account.</li>
              <li>One account per person. Creating multiple accounts to evade bans or restrictions is prohibited.</li>
              <li>You are responsible for all activity that occurs under your account.</li>
            </ul>
            <SubHead>3.3 Verification</SubHead>
            <p className="text-sm text-gray-700">
              We may offer optional photo-based identity verification. Verified status is a trust signal only
              and does not constitute a guarantee of identity, background clearance, or safe conduct.
              Verification decisions are final at our sole discretion.
            </p>
          </Section>

          <Section id="subscriptions" title="4. Subscriptions & Payments">
            <SubHead>4.1 Free Tier</SubHead>
            <p className="text-sm text-gray-700">
              The Free tier allows access to core partner search (up to 12 results per search), messaging,
              basic venue browsing, and session booking, subject to platform-wide limits we may adjust.
            </p>
            <SubHead>4.2 Pro / Elite Subscriptions</SubHead>
            <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm">
              <li>Paid subscriptions unlock unlimited search results, women-only filter, travel mode, priority profile listing, and advanced analytics.</li>
              <li>Subscription fees are billed monthly or annually as selected. All fees are in USD.</li>
              <li>Payments are processed by third-party payment processors. We do not store payment card data.</li>
              <li>Subscriptions auto-renew unless cancelled at least 24 hours before the renewal date.</li>
            </ul>
            <SubHead>4.3 Refund Policy</SubHead>
            <p className="text-sm text-gray-700">
              All subscription fees are non-refundable except where required by applicable law.
              If we terminate your account for violations of these Terms, no refund will be issued.
              Contact us at <a href={`mailto:${EMAIL}`} className="text-brand-green underline underline hover:underline">{EMAIL}</a> for billing disputes.
            </p>
          </Section>

          <Section id="conduct" title="5. Acceptable Use Policy">
            <p>You agree <strong>NOT</strong> to use the Platform to:</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm mt-3">
              <li>Harass, threaten, intimidate, stalk, or abuse any other user</li>
              <li>Post false, misleading, or fraudulent information in your profile or communications</li>
              <li>Impersonate another person or entity, including ActivitySports staff</li>
              <li>Solicit money, sell goods or services, or conduct any commercial activity without written permission</li>
              <li>Send unsolicited commercial messages (spam) to other users</li>
              <li>Scrape, crawl, or programmatically extract data without written authorization</li>
              <li>Attempt to circumvent, bypass, or exploit any security feature or access control</li>
              <li>Upload or distribute malware, viruses, or any malicious code</li>
              <li>Interfere with or disrupt the integrity or performance of the Platform or its servers</li>
              <li>Use the Platform for any illegal purpose or in violation of any local, state, national, or international law</li>
              <li>Collect or harvest personal data about other users without their express consent</li>
              <li>Create accounts for the purpose of evading bans or restrictions</li>
              <li>Facilitate real-world harassment, stalking, or harm to any individual</li>
              <li>Post content that discriminates based on race, gender, religion, nationality, disability, sexual orientation, or age</li>
            </ul>
            <p className="mt-3 text-sm text-gray-700">
              We reserve the right to investigate and take appropriate action, including suspension or
              permanent ban, for any violation of this Acceptable Use Policy.
            </p>
          </Section>

          <Section id="user-content" title="6. User-Generated Content">
            <SubHead>6.1 Your Content</SubHead>
            <p className="text-sm text-gray-700">
              "User Content" means any information, data, text, photos, or other material you submit,
              post, or display through the Platform (including profile information, bio, photos, and messages).
            </p>
            <SubHead>6.2 License Grant</SubHead>
            <p className="text-sm text-gray-700">
              You grant {COMPANY} a worldwide, non-exclusive, royalty-free, sublicensable license to
              use, reproduce, adapt, publish, translate, and distribute your User Content solely for the
              purpose of operating, improving, and promoting the Platform. This license terminates when
              you delete your account (subject to retention periods in our Privacy Policy).
            </p>
            <SubHead>6.3 Content Standards</SubHead>
            <p className="text-sm text-gray-700">
              All User Content must comply with our{" "}
              <Link to="/community" className="text-brand-green underline underline hover:underline">Community Guidelines</Link>.
              We reserve the right to remove any User Content that violates these Terms, without notice.
            </p>
            <SubHead>6.4 No Endorsement</SubHead>
            <p className="text-sm text-gray-700">
              We do not endorse, verify, or warrant the accuracy of any User Content. You are solely
              responsible for your own User Content and the consequences of posting it.
            </p>
          </Section>

          <Section id="intellectual-property" title="7. Intellectual Property">
            <SubHead>7.1 Our IP</SubHead>
            <p className="text-sm text-gray-700">
              The Platform and its original content, features, functionality, branding (including the
              ActivitySports name, logo, and design), software code, databases, and documentation are
              and will remain the exclusive property of {COMPANY}. All rights reserved.
            </p>
            <SubHead>7.2 Restrictions</SubHead>
            <p className="text-sm text-gray-700">
              You may not: (a) copy, modify, or create derivative works based on the Platform;
              (b) reverse engineer or attempt to extract source code; (c) sublicense, sell, or
              commercially exploit the Platform or any portion thereof; (d) remove any copyright,
              trademark, or other proprietary rights notices from the Platform.
            </p>
            <SubHead>7.3 Feedback</SubHead>
            <p className="text-sm text-gray-700">
              Any feedback, suggestions, or ideas you provide to us are provided on a non-confidential
              basis and {COMPANY} shall have the right to use such feedback without restriction or compensation.
            </p>
          </Section>

          <Section id="third-parties" title="8. Third-Party Services & Links">
            <p className="text-sm text-gray-700">
              The Platform may include links to third-party websites or services (including venue websites,
              external booking platforms, and payment processors). These third parties have their own
              privacy policies and terms. We do not control, endorse, or assume responsibility for any
              third-party services, products, or content.
            </p>
            <p className="mt-3 text-sm text-gray-700">
              Your interactions with third parties, including sports venues listed on our Platform,
              are entirely between you and the relevant third party. We are not a party to any
              booking, purchase, or agreement you enter into with a third party.
            </p>
          </Section>

          <Section id="disclaimers" title="9. Disclaimers">
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
              <strong>IMPORTANT — PLEASE READ CAREFULLY.</strong>
            </div>
            <p className="mt-4 text-sm text-gray-700">
              THE PLATFORM IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES
              OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm mt-3">
              <li>We do not warrant that the Platform will be uninterrupted, error-free, or secure at all times.</li>
              <li>We do not verify the identity, credentials, background, or intentions of any user. Meeting other users in person is at your own risk.</li>
              <li>We do not guarantee the accuracy of skill ratings, player profiles, or venue information.</li>
              <li>AI-generated match explanations are provided for informational purposes and are not guaranteed to be accurate.</li>
              <li>We are not responsible for any sports injuries, accidents, or incidents that occur during in-person play sessions arranged through the Platform.</li>
            </ul>
          </Section>

          <Section id="liability" title="10. Limitation of Liability">
            <p className="text-sm text-gray-700">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL {COMPANY.toUpperCase()},
              ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE FOR ANY:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm mt-3">
              <li>Indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, revenue, data, goodwill, or business opportunities</li>
              <li>Personal injury or property damage arising from your use of the Platform or in-person meetups arranged through it</li>
              <li>Unauthorized access to or alteration of your transmissions or data</li>
              <li>Conduct of third parties (including other users) on or off the Platform</li>
            </ul>
            <p className="mt-3 text-sm text-gray-700">
              OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO
              THESE TERMS OR THE PLATFORM SHALL NOT EXCEED THE GREATER OF: (A) THE TOTAL AMOUNTS
              PAID BY YOU TO US IN THE SIX MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED US
              DOLLARS ($100.00).
            </p>
            <p className="mt-3 text-sm text-gray-700">
              Some jurisdictions do not allow the exclusion of certain warranties or limitation
              of liability for incidental or consequential damages, so the above limitations may
              not apply to you.
            </p>
          </Section>

          <Section id="indemnification" title="11. Indemnification">
            <p className="text-sm text-gray-700">
              You agree to defend, indemnify, and hold harmless {COMPANY}, its affiliates, officers,
              directors, employees, and agents from and against any claims, liabilities, damages, losses,
              and expenses (including reasonable attorneys' fees) arising out of or in connection with:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm mt-3">
              <li>Your use of or inability to use the Platform</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any applicable law or the rights of any third party</li>
              <li>Your User Content, including any claims that it infringes intellectual property rights</li>
              <li>Any disputes between you and another user of the Platform</li>
            </ul>
          </Section>

          <Section id="governing-law" title="12. Governing Law & Dispute Resolution">
            <SubHead>12.1 Governing Law</SubHead>
            <p className="text-sm text-gray-700">
              These Terms shall be governed by and construed in accordance with the laws of the{" "}
              {GOVERNING_LAW}, without regard to its conflict of law provisions. Any disputes shall be
              subject to the exclusive jurisdiction of the state and federal courts located in Tarrant
              County, Texas.
            </p>
            <SubHead>12.2 Informal Resolution</SubHead>
            <p className="text-sm text-gray-700">
              Before filing any formal dispute, you agree to attempt to resolve it informally by
              contacting us at <a href={`mailto:${EMAIL}`} className="text-brand-green underline underline hover:underline">{EMAIL}</a>.
              We will try to resolve the dispute informally within 30 days of your notice.
            </p>
            <SubHead>12.3 Binding Arbitration</SubHead>
            <p className="text-sm text-gray-700">
              If informal resolution fails, any dispute arising out of or relating to these Terms or
              the Platform shall be resolved by binding arbitration administered by the American
              Arbitration Association (AAA) under its Consumer Arbitration Rules. The arbitration
              shall take place in Tarrant County, Texas, or via video conference. YOU AGREE TO WAIVE
              YOUR RIGHT TO A JURY TRIAL.
            </p>
            <SubHead>12.4 Class Action Waiver</SubHead>
            <p className="text-sm text-gray-700">
              ANY CLAIMS MUST BE BROUGHT IN YOUR INDIVIDUAL CAPACITY. YOU WAIVE THE RIGHT TO
              PARTICIPATE IN ANY CLASS ACTION, CLASS ARBITRATION, OR REPRESENTATIVE PROCEEDING.
            </p>
          </Section>

          <Section id="termination" title="13. Termination">
            <SubHead>13.1 Termination by You</SubHead>
            <p className="text-sm text-gray-700">
              You may delete your account at any time via your Account Settings page. Deletion
              initiates a 30-day grace period during which your data is recoverable, after which
              it is permanently purged per our Privacy Policy.
            </p>
            <SubHead>13.2 Termination by Us</SubHead>
            <p className="text-sm text-gray-700">
              We may suspend or permanently terminate your account at our sole discretion, with or
              without notice, for: (a) violation of these Terms or Community Guidelines; (b) conduct
              we reasonably believe creates harm or legal risk; (c) extended inactivity; (d) requirement
              by law or government authority.
            </p>
            <SubHead>13.3 Effect of Termination</SubHead>
            <p className="text-sm text-gray-700">
              Upon termination, your right to use the Platform ceases immediately. All provisions
              of these Terms that by their nature should survive termination (including intellectual
              property rights, disclaimers, indemnification, and limitation of liability) shall
              remain in effect.
            </p>
          </Section>

          <Section id="general" title="14. General Provisions">
            <table className="w-full text-sm border-collapse">
              <tbody>
                {[
                  ["Entire Agreement", "These Terms, together with the Privacy Policy and Community Guidelines, constitute the entire agreement between you and the Company."],
                  ["Severability", "If any provision is found unenforceable, the remaining provisions continue in full force."],
                  ["Waiver", "Failure to enforce any provision does not constitute a waiver of our right to enforce it in the future."],
                  ["Assignment", "You may not assign your rights under these Terms without our prior written consent. We may freely assign our rights."],
                  ["Force Majeure", "We are not liable for failures caused by events beyond our reasonable control (natural disasters, internet outages, government actions)."],
                  ["Export Controls", "You may not use the Platform in violation of US export control laws."],
                  ["Language", "These Terms are in English. Any translations are for convenience only; the English version controls."],
                ].map(([term, desc]) => (
                  <tr key={term} className="hover:bg-gray-50 border-b border-gray-100">
                    <td className="p-3 font-semibold text-brand-navy w-1/3 align-top">{term}</td>
                    <td className="p-3 text-gray-600">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section id="contact" title="15. Contact">
            <p className="text-sm text-gray-700">For questions about these Terms, contact us at:</p>
            <div className="mt-4 bg-brand-navy/5 border border-brand-navy/20 rounded-xl p-5 space-y-2">
              <p className="font-bold text-brand-navy">{COMPANY}</p>
              <p className="text-sm text-gray-600">Attn: Legal Team</p>
              <div className="flex items-center gap-2 text-sm text-gray-600"><MapPin size={13} />{ADDRESS}</div>
              <div className="flex items-center gap-2 text-sm text-gray-600"><Mail size={13} /><a href={`mailto:${EMAIL}`} className="text-brand-green underline underline hover:underline">{EMAIL}</a></div>
            </div>
          </Section>

          <div className="pt-8 border-t border-gray-200 text-xs text-gray-400">
            <p>© {new Date().getFullYear()} {COMPANY} · {DBA} · All rights reserved</p>
            <div className="flex gap-4 mt-2">
              <Link to="/privacy" className="hover:text-brand-green">Privacy Policy</Link>
              <Link to="/safety" className="hover:text-brand-green">Safety Center</Link>
              <Link to="/community" className="hover:text-brand-green">Community Guidelines</Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl font-heading font-bold text-brand-navy mb-4 pb-2 border-b border-gray-200">{title}</h2>
      <div className="text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}

function SubHead({ children }) {
  return <h3 className="font-semibold text-brand-navy mt-4 mb-2 text-sm">{children}</h3>;
}
